// React
import React from 'react'

// Next
import Head from 'next/head'
import { GetStaticProps } from 'next'

// Components
import Layout from '@/components/website/core/Layout'
import TextSection from '@/components/website/core/TextSection'
import TopicMemes from '@/components/website/TopicMemes'
import CTAComponent from '@/components/website/core/CTA'
import YouMightAlsoLike from '@/components/website/YouMightAlsoLike'
import LinkedText from '@/components/website/core/LinkedText'

// Utils
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'
import { LinkedContent } from '@/utils/ui/types/linked-content'

// External
import { useTranslation } from 'next-i18next'

const ZoomMeetingsMemes: React.FC = () => {
  const { t } = useTranslation('routes/zoom-meetings-memes')

  return (
    <Layout>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta
          property='og:url'
          content='https://supermeme.ai/memes/zoom-meetings-memes'
        />
        <link
          rel='canonical'
          href='https://supermeme.ai/memes/zoom-meetings-memes'
        />
      </Head>

      <TopicMemes
        heading={t('hero.heading')}
        description={
          <LinkedText
            content={
              t('hero.description', { returnObjects: true }) as LinkedContent
            }
          />
        }
        imageFolder='static/landing-pages/memes-zoom-meeting-memes'
      />

      <div className='mx-auto max-w-4xl space-y-8 py-12 sm:py-16'>
        <YouMightAlsoLike />

        <TextSection
          h2={t('cliches.title')}
          p={
            <ol className='list-decimal space-y-2 pl-5'>
              {(t('cliches.list', { returnObjects: true }) as string[]).map(
                (item, index) => (
                  <li key={index}>{item}</li>
                )
              )}
            </ol>
          }
        />

        <TextSection
          h2={t('toAvoid.title')}
          p={
            <ol className='list-decimal space-y-2 pl-5'>
              {(t('toAvoid.list', { returnObjects: true }) as string[]).map(
                (item, index) => (
                  <li key={index}>{item}</li>
                )
              )}
            </ol>
          }
        />

        <TextSection
          h2={t('preparing.title')}
          p={
            <ol className='list-decimal space-y-2 pl-5'>
              {(t('preparing.list', { returnObjects: true }) as string[]).map(
                (item, index) => (
                  <li key={index}>{item}</li>
                )
              )}
            </ol>
          }
        />

        <TextSection
          h2={t('faqs.title')}
          p={
            <div className='space-y-4'>
              {(
                t('faqs.questions', {
                  returnObjects: true,
                }) as { question: string; answer: string | LinkedContent }[]
              ).map((faq, index) => (
                <p key={index}>
                  <strong>{faq.question}</strong>
                  <br />
                  {typeof faq.answer === 'string' ? (
                    faq.answer
                  ) : (
                    <LinkedText content={faq.answer} />
                  )}
                </p>
              ))}
            </div>
          }
        />

        <CTAComponent />
      </div>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: ['common', 'routes/zoom-meetings-memes', 'components/cta'],
  })
}

export default ZoomMeetingsMemes
